import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Search, Save, X, Edit2, User, Filter, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { getStatusColor, formatDateThai } from '../utils/helpers';

// [✅ เพิ่ม] รับ props user เข้ามาเพื่อใช้ส่งเป็นคนทำรายการ (admin_id)
const AttendanceManager = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    check_in_time: '',
    check_out_time: ''
  });

  useEffect(() => {
    loadData();
    loadDepartments();
  }, [selectedDate]);

  const loadDepartments = async () => {
    try {
      // ⚠️ ข้อมูลแผนกจะถูกกรองตามโรงเรียนอัตโนมัติจาก Backend แล้ว
      const deps = await api.getDepartments();
      setDepartments(deps);
    } catch (error) {
      console.error("Failed to load departments", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // ⚠️ รายชื่อพนักงานและเวลาเข้าออกจะดึงมาเฉพาะของโรงเรียนนี้อัตโนมัติ
      const data = await api.getReports({
        type: 'daily',
        date_from: selectedDate,
        date_to: selectedDate
      });
      setEmployees(data);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      check_in_time: record.check_in ? record.check_in.substring(0, 5) : '',
      check_out_time: record.check_out ? record.check_out.substring(0, 5) : ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        employee_id: editingRecord.employee_id,
        date: selectedDate,
        check_in_time: editForm.check_in_time || null,
        check_out_time: editForm.check_out_time || null,
        // [✅ เพิ่ม] ส่งรหัสพนักงานของ Admin ที่กำลังล็อกอินอยู่ไปเก็บใน Log ด้วย
        admin_id: user?.employee_id 
      };

      await api.saveManualAttendance(payload);
      
      setIsModalOpen(false);
      loadData(); 
      alert('บันทึกข้อมูลเรียบร้อย');
    } catch (error) {
      console.error('Save error:', error);
      alert('บันทึกไม่สำเร็จ: ' + error.message);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = 
      emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_id.includes(searchTerm);
    
    const matchesDept = filterDepartment === 'all' || emp.department === filterDepartment;

    return matchesSearch && matchesDept;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Clock className="h-8 w-8 text-blue-600" />
              จัดการเวลาเข้า-ออกงาน
            </h1>
            <p className="text-gray-500 text-sm mt-1">แก้ไขเวลาของบุคลากร (สถานะจะถูกรีเซ็ตเมื่อมีการแก้ไข)</p>
          </div>
          
          <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
            <Calendar className="h-5 w-5 text-blue-600" />
            <input 
              type="date" 
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border-none focus:ring-0 text-blue-900 font-medium"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="ค้นหาชื่อ หรือ รหัสพนักงาน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            />
          </div>
          
          <div className="md:w-64 relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-white"
            >
              <option value="all">ทุกแผนก</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.department_name}>{dept.department_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">พนักงาน</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">แผนก</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">เวลาเข้า</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">เวลาออก</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">สถานะ</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex justify-center items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce delay-75" />
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce delay-150" />
                    </div>
                  </td>
                </tr>
              ) : filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    ไม่พบข้อมูลพนักงาน
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((emp) => {
                  const shouldHideTime = ['ไปราชการ', 'ไม่มาปฏิบัติงาน'].includes(emp.status) || emp.status.startsWith('ลา');

                  return (
                    <tr key={emp.employee_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                            {emp.first_name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{emp.first_name} {emp.last_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="bg-gray-100 px-2 py-1 rounded-lg text-xs font-medium">
                          {emp.department || '-'}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                          !shouldHideTime && emp.check_in ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {shouldHideTime ? '-' : (emp.check_in ? emp.check_in.substring(0, 5) : '-')}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                          !shouldHideTime && emp.check_out ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {shouldHideTime ? '-' : (emp.check_out ? emp.check_out.substring(0, 5) : '-')}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(emp.status)}`}>
                          {emp.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleEditClick(emp)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                          title="แก้ไขเวลา"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && editingRecord && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-gray-900">แก้ไขข้อมูลเวลา</h3>
                <p className="text-sm text-gray-500">{formatDateThai(selectedDate)}</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              
              {/* Employee Info */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <User className="h-10 w-10 text-blue-600 bg-white p-2 rounded-full shadow-sm" />
                <div>
                  <div className="font-bold text-gray-900">{editingRecord.first_name} {editingRecord.last_name}</div>
                  <div className="text-xs text-blue-600 font-medium">{editingRecord.department}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เวลาเข้างาน</label>
                  <input
                    type="time"
                    value={editForm.check_in_time}
                    onChange={(e) => setEditForm({...editForm, check_in_time: e.target.value})}
                    className="w-full rounded-xl border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เวลาออกงาน</label>
                  <input
                    type="time"
                    value={editForm.check_out_time}
                    onChange={(e) => setEditForm({...editForm, check_out_time: e.target.value})}
                    className="w-full rounded-xl border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 transition-shadow"
                  />
                </div>
              </div>

              <div className="bg-yellow-50 p-3 rounded-xl border border-yellow-200 flex gap-2 items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800">
                  การแก้ไขข้อมูลจะ<strong>รีเซ็ตสถานะและหมายเหตุ</strong> และบันทึกการลงเวลาตามที่ระบุใหม่
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2.5 rounded-xl text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow-lg hover:shadow-blue-500/30 transition-all flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                บันทึกการแก้ไข
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManager;